<template>
	<div class="chat-system-box">
		<el-container>
			<el-header height="60px" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
				<div class="header-title">
					<span class="header-icon">
						<i class="el-icon-bell"></i>
					</span>
					<span>{{ conversation.showName }}</span>
				</div>
			</el-header>
			<el-main class="main" id="chatScrollBox" @scroll="onScroll">
				<div v-if="showEmpty" class="empty-wrap">
					<div class="empty-icon">
						<i class="el-icon-message-solid"></i>
					</div>
					<div class="empty-title">{{ $t('home.systemNotify') }}</div>
					<div class="empty-hint">{{ $t('chat.noSystemNotify') }}</div>
				</div>
				<div v-for="(message, idx) in messages" :key="idx">
					<div v-if="idx >= showMinIdx">
						<div class="time-tip" v-if="message.type == $enums.MESSAGE_TYPE.TIP_TIME">
							<span class="time-tip-text">{{ $date.toTimeText(message.sendTime) }}</span>
						</div>
						<div class="notify-card" @click="onClickMessage(message)"
							v-if="message.type == $enums.MESSAGE_TYPE.SYSTEM_MESSAGE">
							<div class="card-media">
								<img v-if="message.coverUrl" class="card-cover" :src="message.coverUrl" />
								<div v-else class="card-cover-placeholder">
									<div class="placeholder-icon">
										<i class="el-icon-bell"></i>
									</div>
								</div>
							</div>
							<div class="card-body">
								<div class="card-title">{{ message.title }}</div>
								<div v-if="message.intro" class="card-intro">{{ message.intro }}</div>
								<div class="card-footer">
									<span class="footer-text">{{ $t('chat.viewDetail') }}</span>
									<i class="el-icon-arrow-right footer-arrow"></i>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div class="scroll-bottom-spacer"></div>
			</el-main>
			<chat-system-content ref="contentRef"></chat-system-content>
		</el-container>
	</div>
</template>

<script>
import ChatSystemContent from './ChatSystemContent';

export default {
	name: "chatSystem",
	components: { ChatSystemContent },
	props: {
		conversation: {
			type: Object
		}
	},
	data() {
		return {
			messages: [],
			showMinIdx: 5
		}
	},
	computed: {
		showEmpty() {
			return !this.messages.some(m => m.type == this.$enums.MESSAGE_TYPE.SYSTEM_MESSAGE);
		}
	},
	methods: {
		onClickMessage(message) {
			this.$refs.contentRef.show(message.id, message.title);
		},
		readedMessage() {
			if (this.conversation.unreadCount == 0) {
				return;
			}
			const maxSeqNo = this.conversation.maxMessageId;
			const url = `/message/system/readed?maxSeqNo=${maxSeqNo}`
			this.$http({
				url: url,
				method: 'put'
			})
		},
		scrollToBottom() {
			this.$nextTick(() => {
				let div = document.getElementById("chatScrollBox");
				div.scrollTop = div.scrollHeight;
			});
		},
		onScroll(e) {
			const scrollElement = e.target
			const scrollTop = scrollElement.scrollTop
			if (scrollTop < 30) {
				this.showMinIdx = this.showMinIdx > 5 ? this.showMinIdx - 5 : 0;
			}
		},
	},
	watch: {
		conversation: {
			async handler() {
				this.messages = await this.$db.findMessageByConvKey(this.conversation.key);
				let size = this.messages.length;
				this.showMinIdx = size > 5 ? size - 5 : 0;
				this.readedMessage();
				this.scrollToBottom();
			},
			immediate: true
		}
	},
	mounted() {
		let div = document.getElementById("chatScrollBox");
		div.addEventListener('scroll', this.onScroll)
	}
}
</script>

<style lang="scss" scoped>
.chat-system-box {
	display: flex;
	flex-direction: column;
	position: relative;
	width: 100%;
	background: var(--im-background-page);

	.el-header {
		display: flex;
		align-items: center;
		padding: 0 20px;
		line-height: 60px;
		border-bottom: var(--im-border);
		background: var(--im-background);

		.header-title {
			display: flex;
			align-items: center;
			gap: 10px;
			font-size: var(--im-font-size-larger);
			font-weight: 600;
			color: var(--im-text-color);

			.header-icon {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 32px;
				height: 32px;
				border-radius: 10px;
				background: linear-gradient(135deg, var(--im-color-primary-light-2) 0%, var(--im-color-primary) 100%);
				color: #fff;
				font-size: 16px;
				box-shadow: 0 2px 8px rgba(65, 105, 222, 0.25);
			}
		}
	}

	.main {
		background: var(--im-background-page);
		padding: 16px 32px 0;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		overflow-y: auto;

		.scroll-bottom-spacer {
			height: 24px;
			flex-shrink: 0;
		}

		.empty-wrap {
			display: flex;
			flex-direction: column;
			align-items: center;
			padding: 120px 40px 80px;
			text-align: center;

			.empty-icon {
				width: 80px;
				height: 80px;
				border-radius: 50%;
				background: var(--im-background);
				display: flex;
				align-items: center;
				justify-content: center;
				margin-bottom: 20px;
				box-shadow: var(--im-box-shadow-lighter);
				border: var(--im-border-light);

				i {
					font-size: 36px;
					color: var(--im-color-primary);
				}
			}

			.empty-title {
				font-size: var(--im-font-size-large);
				color: var(--im-text-color);
				font-weight: 600;
				margin-bottom: 8px;
			}

			.empty-hint {
				font-size: var(--im-font-size-small);
				color: var(--im-text-color-lighter);
				line-height: 1.6;
			}
		}

		.time-tip {
			display: flex;
			justify-content: center;
			padding: 4px 0 12px;
			width: 100%;

			.time-tip-text {
				font-size: var(--im-font-size-smaller);
				color: var(--im-text-color-lighter);
				background: rgba(15, 23, 42, 0.05);
				padding: 4px 14px;
				border-radius: 100px;
				line-height: 1.4;
			}
		}

		.notify-card {
			width: 100%;
			background: var(--im-background);
			border-radius: 16px;
			overflow: hidden;
			margin-bottom: 16px;
			box-shadow: var(--im-box-shadow-lighter);
			border: var(--im-border-light);
			cursor: pointer;
			transition: transform 0.2s ease, box-shadow 0.2s ease;

			&:hover {
				transform: translateY(-2px);
				box-shadow: var(--im-box-shadow);
			}

			&:active {
				transform: translateY(0);
				opacity: 0.95;
			}

			.card-media {
				position: relative;
				width: 100%;
				height: 220px;
				overflow: hidden;

				.card-cover {
					width: 100%;
					height: 100%;
					object-fit: cover;
					display: block;
					transition: transform 0.3s ease;
				}

				.card-cover-placeholder {
					width: 100%;
					height: 100%;
					background: linear-gradient(135deg, var(--im-color-primary-light-2) 0%, var(--im-color-primary) 100%);
					display: flex;
					align-items: center;
					justify-content: center;

					.placeholder-icon {
						width: 56px;
						height: 56px;
						border-radius: 50%;
						background: rgba(255, 255, 255, 0.2);
						display: flex;
						align-items: center;
						justify-content: center;
						border: 2px solid rgba(255, 255, 255, 0.35);

						i {
							font-size: 26px;
							color: #fff;
						}
					}
				}
			}

			&:hover .card-cover {
				transform: scale(1.03);
			}

			.card-body {
				padding: 20px 24px 0;

				.card-title {
					font-size: var(--im-font-size-larger);
					font-weight: 600;
					color: var(--im-text-color);
					line-height: 1.45;
					margin-bottom: 8px;
					overflow: hidden;
					display: -webkit-box;
					-webkit-box-orient: vertical;
					-webkit-line-clamp: 2;
				}

				.card-intro {
					font-size: var(--im-font-size-small);
					color: var(--im-text-color-lighter);
					line-height: 1.65;
					overflow: hidden;
					display: -webkit-box;
					-webkit-box-orient: vertical;
					-webkit-line-clamp: 3;
					margin-bottom: 4px;
				}

				.card-footer {
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 12px 0 14px;
					margin-top: 8px;
					border-top: var(--im-border-light);

					.footer-text {
						font-size: var(--im-font-size-smaller);
						color: var(--im-color-primary);
						font-weight: 500;
					}

					.footer-arrow {
						font-size: 12px;
						color: var(--im-color-primary);
						transition: transform 0.2s ease;
					}
				}
			}

			&:hover .card-footer .footer-arrow {
				transform: translateX(3px);
			}
		}
	}
}
</style>
